import React, { useState, useEffect } from 'react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import {
    faUsers, faChalkboardTeacher, faGraduationCap, faCreditCard,
    faChartLine, faCalendarAlt, faCheckCircle, faClock,
    faArrowTrendUp, faArrowTrendDown, faBriefcase, faChartPie, faArrowUp, faArrowDown, faFileExport
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion } from 'framer-motion';
import ApiServices from '../Services/ApiServices';
import { downloadAsCSV } from '../../Helpers/excelHelpers';

const COLORS = {
    primary: '#6366F1',
    secondary: '#10B981',
    warning: '#6366f1',
    danger: '#EF4444',
    info: '#3B82F6',
    purple: '#8B5CF6',
    pink: '#EC4899',
    teal: '#14B8A6'
};

const CHART_COLORS = [COLORS.primary, COLORS.secondary, COLORS.warning, COLORS.purple, COLORS.pink, COLORS.info];

const PERIOD_OPTIONS = [
    { value: 1, label: 'الأسبوع الماضي', key: 'LastWeek' },
    { value: 2, label: 'الشهر الماضي', key: 'LastMonth' },
    { value: 3, label: 'الربع الأخير', key: 'LastQuarter' },
    { value: 4, label: 'نصف السنة الماضي', key: 'LastSemiAnnual' },
    { value: 5, label: 'السنة الماضية', key: 'LastAnnual' },
];

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="custom-tooltip">
                <p className="tooltip-label">{label}</p>
                <div className="tooltip-items">
                    {payload.map((item, index) => (
                        <div key={index} className="tooltip-item">
                            <span className="tooltip-dot" style={{ backgroundColor: item.color }}></span>
                            <span className="tooltip-name">{item.name}:</span>
                            <span className="tooltip-value">{item.value}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    }
    return null;
};

const AdminStats = () => {
    const [loading, setLoading] = useState(true);
    const [periodType, setPeriodType] = useState(1);
    const [data, setData] = useState(null);

    useEffect(() => {
        fetchStats();
    }, [periodType]);

    const fetchStats = async () => {
        setLoading(true);
        try {
            const response = await ApiServices.get(`Dashboard/State?type=${periodType}`);
            setData(response);
        } catch (error) {

        } finally {
            setLoading(false);
        }
    };

    if (loading && !data) {
        return (
            <div className="stats-loading">
                <div className="modern-spinner">
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                </div>
                <p>جاري تحضير البيانات الذكية...</p>
            </div>
        );
    }

    const { stats, user_growth_trend, plan_subscription_trend, transaction_trend, top_plans } = data || {};

    const handleExport = () => {
        if (!stats) return;

        const headers = {
            applied_period: 'الفترة المطبقة',
            period_start_date: 'تاريخ البدء',
            period_end_date: 'تاريخ الانتهاء',
            total_teachers: 'إجمالي المعلمين',
            total_active_teachers: 'المعلمين النشطين',
            new_teachers_in_period: 'المعلمين الجدد في الفترة',
            total_students: 'إجمالي الطلاب',
            total_active_students: 'الطلاب النشطين',
            new_students_in_period: 'الطلاب الجدد في الفترة',
            total_visible_courses: 'الكورسات المرئية',
            total_course_subscriptions: 'إجمالي اشتراكات الكورسات',
            overall_course_completion_rate_percent: 'معدل إكمال الكورسات %',
            total_plan_subscriptions: 'إجمالي اشتراكات الباقات',
            new_plan_subscriptions_in_period: 'اشتراكات الباقات الجديدة',
            total_renewed_transactions: 'المعاملات المجددة',
            total_subscription_requests: 'طلبات الاشتراك',
            approved_subscription_requests: 'الطلبات المقبولة',
            rejected_subscription_requests: 'الطلبات المرفوضة'
        };

        const exportData = [{
            ...stats,
            applied_period: PERIOD_OPTIONS.find(p => p.value === periodType)?.label,
            period_start_date: new Date(stats.period_start_date).toLocaleDateString('ar-EG'),
            period_end_date: new Date(stats.period_end_date).toLocaleDateString('ar-EG'),
        }];

        downloadAsCSV(exportData, `platform-stats-report-${formatDateFilename(new Date())}`, headers);
    };

    const formatDateFilename = (date) => {
        return date.toISOString().split('T')[0];
    };

    const StatCard = ({ title, value, icon, color, trend, percentage }) => (
        <motion.div
            className="modern-stat-card"
            whileHover={{ y: -8, transition: { duration: 0.2 } }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
        >
            <div className="stat-card-glow" style={{ backgroundColor: color }}></div>
            <div className="stat-card-inner">
                <div className="stat-top">
                    <div className="stat-icon-box" style={{ backgroundColor: `${color}15`, color: color }}>
                        <FontAwesomeIcon icon={icon} />
                    </div>
                    {percentage && (
                        <div className={`stat-badge ${trend === 'up' ? 'positive' : 'negative'}`}>
                            <FontAwesomeIcon icon={trend === 'up' ? faArrowUp : faArrowDown} />
                            {percentage}%
                        </div>
                    )}
                </div>
                <div className="stat-bottom">
                    <h3 className="stat-value">{value.toLocaleString()}</h3>
                    <p className="stat-title">{title}</p>
                </div>
            </div>
        </motion.div>
    );

    return (
        <div className="modern-stats-container">
            {/* Dynamic Header */}
            <div className="dashboard-header">
                <div className="header-text">
                    <h1>لوحة التحليلات المتقدمة</h1>
                    <p>مراقبة أداء المنصة وتحليل البيانات في الوقت الفعلي</p>
                </div>
                <div className="period-pill-container" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div className="pills">
                        {PERIOD_OPTIONS.map(opt => (
                            <button
                                key={opt.value}
                                className={`period-pill ${periodType === opt.value ? 'active' : ''}`}
                                onClick={() => setPeriodType(opt.value)}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                    <button className="export-stats-btn" onClick={handleExport}>
                        <FontAwesomeIcon icon={faFileExport} />
                        تصدير البيانات
                    </button>
                </div>
            </div>

            {stats && (
                <div className="stats-grid-row">
                    <StatCard title="إجمالي المعلمين" value={stats.total_teachers} icon={faChalkboardTeacher} color={COLORS.primary} trend="up" percentage={12} />
                    <StatCard title="إجمالي الطلاب" value={stats.total_students} icon={faUsers} color={COLORS.secondary} trend="up" percentage={45} />
                    <StatCard title="الطلاب النشطين" value={stats.total_active_students} icon={faCheckCircle} color={COLORS.warning} trend="up" percentage={8} />
                    <StatCard title="المعاملات المالية" value={stats.total_renewed_transactions} icon={faCreditCard} color={COLORS.purple} trend="down" percentage={2} />
                </div>
            )}

            <div className="main-charts-grid">
                {/* User Growth - Premium Area Chart */}
                <div className="chart-card-full">
                    <div className="chart-info">
                        <h2>نمو القوى العاملة والطلاب</h2>
                        <p>تحليل تدفق المستخدمين الجدد للمنصة</p>
                    </div>
                    <div className="chart-canvas">
                        <ResponsiveContainer width="100%" height={350}>
                            <AreaChart data={user_growth_trend}>
                                <defs>
                                    <linearGradient id="colorTeachers" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3} />
                                        <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="colorStudents" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={COLORS.secondary} stopOpacity={0.3} />
                                        <stop offset="95%" stopColor={COLORS.secondary} stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 12 }} dy={10} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 12 }} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend iconType="circle" wrapperStyle={{ paddingTop: '20px' }} />
                                <Area
                                    type="monotone"
                                    dataKey="new_teachers"
                                    name="معلمون جدد"
                                    stroke={COLORS.primary}
                                    strokeWidth={4}
                                    fillOpacity={1}
                                    fill="url(#colorTeachers)"
                                />
                                <Area
                                    type="monotone"
                                    dataKey="new_students"
                                    name="طلاب جدد"
                                    stroke={COLORS.secondary}
                                    strokeWidth={4}
                                    fillOpacity={1}
                                    fill="url(#colorStudents)"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Plan Trend - Modern Bar Chart */}
                <div className="chart-card-half">
                    <div className="chart-info">
                        <h2>تدفق الاشتراكات</h2>
                        <p>أداء باقات الاشتراك المختلفة</p>
                    </div>
                    <div className="chart-canvas">
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={plan_subscription_trend}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 12 }} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 12 }} />
                                <Tooltip cursor={{ fill: '#F1F5F9' }} content={<CustomTooltip />} />
                                <Bar dataKey="new_subscriptions" name="اشتراكات" fill={COLORS.primary} radius={[6, 6, 0, 0]} barSize={20} />
                                <Bar dataKey="promoted_count" name="ترقيات" fill={COLORS.info} radius={[6, 6, 0, 0]} barSize={20} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Transaction History - Smooth LineChart */}
                <div className="chart-card-half">
                    <div className="chart-info">
                        <h2>النشاط المالي</h2>
                        <p>نظرة على حركة المعاملات المجددة والجديدة</p>
                    </div>
                    <div className="chart-canvas">
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={transaction_trend}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 12 }} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 12 }} />
                                <Tooltip content={<CustomTooltip />} />
                                <Line
                                    type="monotone"
                                    dataKey="total_transactions"
                                    name="الإجمالي"
                                    stroke={COLORS.purple}
                                    strokeWidth={4}
                                    dot={{ r: 6, fill: COLORS.purple, strokeWidth: 2, stroke: '#fff' }}
                                    activeDot={{ r: 8 }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Distribution Donuts */}
                <div className="distribution-row full-width">
                    <div className="chart-card-third">
                        <h3>توزيع المعلمين</h3>
                        <ResponsiveContainer width="100%" height={240}>
                            <PieChart>
                                <Pie
                                    data={[
                                        { name: 'نشط', value: stats.total_active_teachers },
                                        { name: 'معلق', value: stats.total_pending_teachers },
                                    ]}
                                    innerRadius={70} outerRadius={90} paddingAngle={8} dataKey="value" stroke="none"
                                >
                                    <Cell fill={COLORS.secondary} />
                                    <Cell fill={COLORS.warning} />
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="donut-center-text">
                            <span className="total-val">{stats.total_teachers}</span>
                            <span className="total-lab">معلم</span>
                        </div>
                    </div>

                    <div className="chart-card-third">
                        <h3>وضوح المحتوى</h3>
                        <ResponsiveContainer width="100%" height={240}>
                            <PieChart>
                                <Pie
                                    data={[
                                        { name: 'مرئي', value: stats.total_visible_courses },
                                        { name: 'مخفي', value: stats.total_hidden_courses },
                                    ]}
                                    innerRadius={70} outerRadius={90} paddingAngle={8} dataKey="value" stroke="none"
                                >
                                    <Cell fill={COLORS.primary} />
                                    <Cell fill={COLORS.pink} />
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="donut-center-text">
                            <span className="total-val">{stats.total_visible_courses + stats.total_hidden_courses}</span>
                            <span className="total-lab">كورس</span>
                        </div>
                    </div>

                    <div className="chart-card-third">
                        <h3>تفاعل الطلاب</h3>
                        <ResponsiveContainer width="100%" height={240}>
                            <PieChart>
                                <Pie
                                    data={[
                                        { name: 'متفاعل', value: stats.total_active_students },
                                        { name: 'خامل', value: stats.total_students - stats.total_active_students },
                                    ]}
                                    innerRadius={70} outerRadius={90} paddingAngle={8} dataKey="value" stroke="none"
                                >
                                    <Cell fill={COLORS.info} />
                                    <Cell fill={COLORS.danger} />
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="donut-center-text">
                            <span className="total-val">{stats.total_students}</span>
                            <span className="total-lab">طالب</span>
                        </div>
                    </div>
                </div>

                {/* Top Plans Section - Modern Infographic Style */}
                <div className="chart-card-full">
                    <div className="chart-info-top">
                        <div className="title-area">
                            <h2>أداء باقات الاشتراك</h2>
                            <p>تحليل تفصيلي لأكثر الباقات طلباً وتفاعلاً</p>
                        </div>
                        <div className="stat-summary-pill">
                            <FontAwesomeIcon icon={faBriefcase} />
                            <span>إجمالي الخطط: {top_plans?.length || 0}</span>
                        </div>
                    </div>

                    <div className="premium-plans-grid">
                        <div className="plans-chart-main">
                            <ResponsiveContainer width="100%" height={400}>
                                <BarChart
                                    layout="vertical"
                                    data={top_plans?.slice(0, 5)}
                                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                                    barGap={8}
                                >
                                    <XAxis type="number" hide />
                                    <YAxis
                                        dataKey="plan_name"
                                        type="category"
                                        hide
                                    />
                                    <Tooltip
                                        content={<CustomTooltip />}
                                        cursor={{ fill: 'rgba(241, 245, 249, 0.5)' }}
                                    />
                                    <Bar
                                        dataKey="total_subscriptions"
                                        name="إجمالي الاشتراكات"
                                        fill={COLORS.primary}
                                        radius={[0, 10, 10, 0]}
                                        barSize={18}
                                        label={(props) => {
                                            const { y, width, payload } = props;
                                            if (!payload || !payload.plan_name) return null;
                                            return (
                                                <g>
                                                    <text
                                                        x={0}
                                                        y={y - 10}
                                                        fill="#1E293B"
                                                        fontSize={14}
                                                        fontWeight={700}
                                                        textAnchor="start"
                                                        className="chart-label-text"
                                                    >
                                                        {payload.plan_name}
                                                    </text>
                                                    <text
                                                        x={width + 10}
                                                        y={y + 14}
                                                        fill="#64748B"
                                                        fontSize={12}
                                                        fontWeight={600}
                                                    >
                                                        {payload.total_subscriptions}
                                                    </text>
                                                </g>
                                            );
                                        }}
                                    />
                                    <Bar
                                        dataKey="promoted_subscriptions"
                                        name="مرقية"
                                        fill={COLORS.secondary}
                                        radius={[0, 10, 10, 0]}
                                        barSize={18}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="plans-list-side">
                            {top_plans?.slice(0, 4).map((plan, idx) => (
                                <div key={idx} className="mini-plan-card">
                                    <div className="plan-rank">#{idx + 1}</div>
                                    <div className="plan-details">
                                        <h4>{plan.plan_name}</h4>
                                        <div className="plan-meta">
                                            <span className="price">{plan.price_discounted} ر.س</span>
                                            <span className="period">{plan.plan_period}</span>
                                        </div>
                                    </div>
                                    <div className="plan-stats">
                                        <div className="stat-item">
                                            <span className="val">{plan.total_subscriptions}</span>
                                            <span className="lab">كلي</span>
                                        </div>
                                        <div className="stat-item highlight">
                                            <span className="val">{plan.promoted_subscriptions}</span>
                                            <span className="lab">مرقى</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                .chart-info-top {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 30px;
                }

                .stat-summary-pill {
                    background: #F1F5F9;
                    padding: 8px 16px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    color: #475569;
                    font-weight: 600;
                    font-size: 0.9rem;
                }

                .premium-plans-grid {
                    display: grid;
                    grid-template-columns: 1.5fr 1fr;
                    gap: 40px;
                    align-items: center;
                }

                .plans-list-side {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }

                .mini-plan-card {
                    background: #F8FAFC;
                    padding: 16px;
                    border-radius: 20px;
                    display: flex;
                    align-items: center;
                    gap: 15px;
                    border: 1px solid #F1F5F9;
                    transition: all 0.2s ease;
                }

                .mini-plan-card:hover {
                    background: white;
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05);
                    border-color: #E2E8F0;
                }

                .plan-rank {
                    width: 36px;
                    height: 36px;
                    background: #6366F1;
                    color: white;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 800;
                    font-size: 0.9rem;
                }

                .plan-details {
                    flex: 1;
                }

                .plan-details h4 {
                    margin: 0;
                    font-size: 1rem;
                    color: #1E293B;
                    font-weight: 700;
                }

                .plan-meta {
                    display: flex;
                    gap: 10px;
                    font-size: 0.8rem;
                    color: #64748B;
                    margin-top: 4px;
                }

                .plan-stats {
                    display: flex;
                    gap: 20px;
                }

                .stat-item {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }

                .stat-item.highlight .val {
                    color: #10B981;
                }

                .stat-item .val {
                    font-weight: 800;
                    font-size: 1rem;
                    color: #1E293B;
                }

                .stat-item .lab {
                    font-size: 0.7rem;
                    color: #94A3B8;
                    text-transform: uppercase;
                    font-weight: 700;
                }

                .chart-label-text {
                      font-family: "Zain", sans-serif;
                }

                @media (max-width: 992px) {
                    .premium-plans-grid {
                        grid-template-columns: 1fr;
                    }
                }
        .modern-stats-container {
          padding: 20px;
          background: #F8FAFC;
          min-height: 100vh;
          direction: rtl;
          font-family: 'Outfit', 'Cairo', sans-serif;
        }

        /* Header Styles */
        .dashboard-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          margin-bottom: 40px;
          flex-wrap: wrap;
          gap: 20px;
        }

        .header-text h1 {
          font-size: 2.2rem;
          color: #0F172A;
          font-weight: 800;
          margin: 0;
          letter-spacing: -0.02em;
        }

        .header-text p {
          color: #64748B;
          font-size: 1.1rem;
          margin-top: 8px;
        }

        .period-pill-container {
          display: flex;
          background: white;
          padding: 6px;
          border-radius: 100px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
          border: 1px solid #E2E8F0;
        }

        .period-pill {
          padding: 10px 24px;
          border-radius: 100px;
          border: none;
          background: transparent;
          color: #64748B;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            font-family: "Zain", sans-serif;
        }

        .period-pill.active {
          background: #6366F1;
          color: white;
          box-shadow: 0 10px 15px -3px rgba(99, 102, 241, 0.3);
        }

        .export-stats-btn {
          background: #10B981;
          color: white;
          border: none;
          padding: 10px 24px;
          border-radius: 100px;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 6px -1px rgba(16, 185, 129, 0.2);
            font-family: "Zain", sans-serif;
        }

        .export-stats-btn:hover {
          background: #059669;
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(16, 185, 129, 0.3);
        }

        /* Stat Cards */
        .stats-grid-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: 24px;
          margin-bottom: 40px;
        }

        .modern-stat-card {
          background: white;
          border-radius: 24px;
          padding: 28px;
          position: relative;
          overflow: hidden;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px -1px rgba(0, 0, 0, 0.01);
          border: 1px solid #F1F5F9;
        }

        .stat-card-glow {
          position: absolute;
          width: 150px;
          height: 150px;
          top: -100px;
          right: -100px;
          filter: blur(80px);
          opacity: 0.15;
          pointer-events: none;
        }

        .stat-top {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 24px;
        }

        .stat-icon-box {
          width: 56px;
          height: 56px;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.4rem;
        }

        .stat-badge {
          padding: 6px 12px;
          border-radius: 100px;
          font-size: 0.85rem;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .stat-badge.positive {
          background: #DCFCE7;
          color: #15803D;
        }

        .stat-badge.negative {
          background: #FEE2E2;
          color: #B91C1C;
        }

        .stat-value {
          font-size: 2rem;
          font-weight: 800;
          color: #0F172A;
          margin: 0;
          letter-spacing: -0.02em;
        }

        .stat-title {
          color: #64748B;
          font-size: 1rem;
          margin: 4px 0 0 0;
          font-weight: 500;
        }

        /* Charts Layout */
        .main-charts-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 24px;
        }

        .chart-card-full, .chart-card-half, .chart-card-third {
          background: white;
          border-radius: 32px;
          padding: 32px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02);
          border: 1px solid #F1F5F9;
        }

        .chart-card-full {
          grid-column: span 2;
        }

        .chart-info h2 {
          font-size: 1.4rem;
          color: #1E293B;
          margin: 0;
          font-weight: 700;
        }

        .chart-info p {
          color: #64748B;
          font-size: 0.95rem;
          margin: 6px 0 24px 0;
        }

        .distribution-row {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          grid-column: span 2;
        }

        .chart-card-third {
          position: relative;
          text-align: center;
        }

        .chart-card-third h3 {
          font-size: 1.1rem;
          color: #1E293B;
          margin-bottom: 30px;
        }

        .donut-center-text {
          position: absolute;
          top: 62%;
          left: 50%;
          transform: translate(-50%, -50%);
          display: flex;
          flex-direction: column;
          align-items: center;
          pointer-events: none;
        }

        .total-val {
          font-size: 1.8rem;
          font-weight: 800;
          color: #0F172A;
        }

        .total-lab {
          font-size: 0.85rem;
          color: #64748B;
          font-weight: 600;
          margin-top: -4px;
        }

        /* Custom Tooltip */
        .custom-tooltip {
          background: rgba(15, 23, 42, 0.95);
          backdrop-filter: blur(8px);
          padding: 16px;
          border-radius: 16px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          color: white;
          min-width: 150px;
        }

        .tooltip-label {
          font-weight: 700;
          margin-bottom: 12px;
          font-size: 0.95rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          padding-bottom: 8px;
        }

        .tooltip-item {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 6px;
        }

        .tooltip-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
        }

        .tooltip-name {
          font-size: 0.85rem;
          opacity: 0.8;
        }

        .tooltip-value {
          font-weight: 700;
          margin-right: auto;
          font-family: 'Outfit', sans-serif;
        }

        /* Loading */
        .stats-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 80vh;
        }

        .modern-spinner {
          display: flex;
          gap: 8px;
          margin-bottom: 20px;
        }

        .dot {
          width: 12px;
          height: 12px;
          background: #6366F1;
          border-radius: 50%;
          animation: bounce 0.6s infinite alternate;
        }

        .dot:nth-child(2) { animation-delay: 0.2s; }
        .dot:nth-child(3) { animation-delay: 0.4s; }

        @keyframes bounce {
          to { transform: translateY(-10px); opacity: 0.3; }
        }

        /* Mobile Responsiveness Enhancements */
        @media (max-width: 1200px) {
          .main-charts-grid { grid-template-columns: 1fr; }
          .chart-card-full, .distribution-row { grid-column: auto; }
          .distribution-row { grid-template-columns: 1fr 1fr; }
        }

        @media (max-width: 992px) {
          .premium-plans-grid { grid-template-columns: 1fr; gap: 30px; }
        }

        @media (max-width: 768px) {
          .modern-stats-container { padding: 12px; }
          
          .dashboard-header { 
            flex-direction: column; 
            align-items: center; 
            text-align: center; 
            gap: 24px;
            margin-bottom: 30px;
          }

          .header-text h1 { font-size: 1.8rem; }
          .header-text p { font-size: 0.95rem; }

          .period-pill-container { 
            width: 100%; 
            flex-direction: column; 
            background: transparent; 
            border: none; 
            box-shadow: none;
            padding: 0;
            gap: 15px;
          }

          .period-pill-container .pills {
            display: flex;
            overflow-x: auto;
            width: 100%;
            padding: 6px;
            background: white;
            border-radius: 100px;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
            border: 1px solid #E2E8F0;
            scrollbar-width: none; /* Hide scrollbar Firefox */
          }
          
          .period-pill-container .pills::-webkit-scrollbar {
            display: none; /* Hide scrollbar Chrome/Safari */
          }

          .period-pill {
            white-space: nowrap;
            padding: 8px 16px;
            font-size: 0.85rem;
          }

          .export-stats-btn {
            width: 100%;
            justify-content: center;
          }

          .distribution-row { grid-template-columns: 1fr; }
          
          .modern-stat-card { padding: 20px; text-align: center; }
          .stat-top { justify-content: center; position: relative; }
          .stat-badge { position: absolute; top: 0; left: 0; }
          .stat-value { font-size: 1.6rem; }

          .chart-card-full, .chart-card-half, .chart-card-third {
            padding: 20px;
            border-radius: 24px;
          }
          
          .chart-info h2 { font-size: 1.2rem; }
          
          .mini-plan-card { padding: 12px; }
          .stat-item .val { font-size: 0.9rem; }
        }

        @media (max-width: 480px) {
          .stat-badge { display: none; } /* Hide badges on very small screens to save space */
          .header-text h1 { font-size: 1.5rem; }
          .chart-canvas { height: 250px; }
        }
      `}</style>
        </div>
    );
};

export default AdminStats;
